import { supabase, supabaseConfigured } from './supabase';

export type PlatformRecord = {
  id: string;
  slug: string;
  name: string;
  domain: string;
  tagline: string;
  description: string | null;
  status: 'offline' | 'booting' | 'online' | 'maintenance';
  metadata: Record<string, unknown>;
};

export type NodeRecord = {
  id: string;
  platform_id: string | null;
  slug: string;
  name: string;
  description: string | null;
  status: 'offline' | 'booting' | 'online' | 'degraded';
  metadata: Record<string, unknown>;
};

export type AgentRecord = {
  id: string;
  node_id: string;
  slug: string;
  name: string;
  role: string;
  status: 'offline' | 'available' | 'busy' | 'paused';
};

export type ToolRecord = {
  id: string;
  slug: string;
  name: string;
  category: string;
  status: 'offline' | 'available' | 'restricted';
};

export type AgentToolRecord = {
  agent_id: string;
  tool_id: string;
};

export type TaskTemplateStep = {
  key: string;
  label: string;
  type: 'plan' | 'agent' | 'tool' | 'verify' | 'approval' | 'deliver';
};

export type IntentProfileRecord = {
  id: string;
  node_id: string;
  slug: string;
  label: string;
  title: string;
  description: string;
  agent_slugs: string[];
  tool_slugs: string[];
  task_template: TaskTemplateStep[];
  approval_mode: 'none' | 'optional' | 'required';
  sort_order: number;
  metadata: Record<string, unknown>;
};

export type CatalogSnapshot = {
  platform: PlatformRecord | null;
  node: NodeRecord | null;
  agents: AgentRecord[];
  tools: ToolRecord[];
  agentTools: AgentToolRecord[];
  intents: IntentProfileRecord[];
};

export type CatalogStatus =
  | 'missing_env'
  | 'connecting'
  | 'connected'
  | 'error';

export async function loadCatalog(): Promise<CatalogSnapshot> {
  if (!supabaseConfigured || !supabase) {
    throw new Error('Supabase browser configuration is missing.');
  }

  const [
    platformResult,
    nodeResult,
    agentsResult,
    toolsResult,
    agentToolsResult,
    intentsResult,
  ] = await Promise.all([
    supabase
      .from('platforms')
      .select('id,slug,name,domain,tagline,description,status,metadata')
      .eq('slug', 'aimatrix-one')
      .maybeSingle(),
    supabase
      .from('nodes')
      .select('id,platform_id,slug,name,description,status,metadata')
      .eq('slug', 'node-001')
      .maybeSingle(),
    supabase
      .from('agents')
      .select('id,node_id,slug,name,role,status')
      .order('name'),
    supabase
      .from('tools')
      .select('id,slug,name,category,status')
      .order('name'),
    supabase
      .from('agent_tools')
      .select('agent_id,tool_id'),
    supabase
      .from('intent_profiles')
      .select('id,node_id,slug,label,title,description,agent_slugs,tool_slugs,task_template,approval_mode,sort_order,metadata')
      .order('sort_order'),
  ]);

  const firstError =
    platformResult.error ||
    nodeResult.error ||
    agentsResult.error ||
    toolsResult.error ||
    agentToolsResult.error ||
    intentsResult.error;

  if (firstError) {
    throw firstError;
  }

  return {
    platform: (platformResult.data as PlatformRecord | null) ?? null,
    node: (nodeResult.data as NodeRecord | null) ?? null,
    agents: (agentsResult.data as AgentRecord[] | null) ?? [],
    tools: (toolsResult.data as ToolRecord[] | null) ?? [],
    agentTools:
      (agentToolsResult.data as AgentToolRecord[] | null) ?? [],
    intents: (intentsResult.data as IntentProfileRecord[] | null) ?? [],
  };
}
